import { Editor } from '../core/Editor';
import { Exporter } from './Exporter';

export class SaveForWebModal {
  private editor: Editor;
  private container: HTMLDivElement | null = null;
  private previewImg: HTMLImageElement | null = null;
  private sizeDisplay: HTMLDivElement | null = null;
  private currentBlob: Blob | null = null;

  // Settings
  private format: 'webp' | 'jpeg' | 'png' | 'avif' = 'webp';
  private quality: number = 80;
  private scale: number = 100;
  private preserveAlpha: boolean = true;
  private filename: string = 'optimized-web';

  constructor(editor: Editor) {
    this.editor = editor;
  }

  public open() {
    this.createModal();
    this.updatePreview();
  }

  public close() {
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }

  private createModal() {
    this.close();

    const overlay = document.createElement('div');
    overlay.className = 'modal-backdrop';
    overlay.id = 'save-for-web-modal';

    overlay.innerHTML = `
      <div class="modal-card save-web-card">
        <div class="modal-header">
          <div class="modal-title-group">
            <div class="modal-icon">⚡</div>
            <div>
              <h3>Save for Web & Optimize</h3>
              <p class="modal-subtitle">Reduce image payload with intelligent multi-format compression</p>
            </div>
          </div>
          <button class="modal-close-btn" id="close-save-web">&times;</button>
        </div>

        <div class="save-web-body">
          <!-- Left: Preview Canvas -->
          <div class="save-web-preview-pane">
            <div class="preview-checkerboard">
              <img id="save-web-img" alt="Compressed Preview" />
            </div>
            <div class="preview-stats-bar" id="save-web-stats">
              Computing compression...
            </div>
          </div>

          <!-- Right: Compression Controls -->
          <div class="save-web-controls-pane">
            <!-- Format selector -->
            <div class="control-group">
              <label class="control-label">Export Format</label>
              <div class="format-buttons">
                <button type="button" class="format-btn active" data-fmt="webp">WebP</button>
                <button type="button" class="format-btn" data-fmt="jpeg">JPEG</button>
                <button type="button" class="format-btn" data-fmt="png">PNG</button>
                <button type="button" class="format-btn" data-fmt="avif">AVIF</button>
              </div>
            </div>

            <!-- Quality slider -->
            <div class="control-group" id="quality-control-group">
              <div class="label-with-value">
                <label class="control-label">Quality</label>
                <span class="value-badge" id="quality-val">80%</span>
              </div>
              <input type="range" id="quality-slider" min="5" max="100" value="80" class="styled-range" />
            </div>

            <!-- Image Scale -->
            <div class="control-group">
              <div class="label-with-value">
                <label class="control-label">Scale Dimensions</label>
                <span class="value-badge" id="scale-val">100%</span>
              </div>
              <input type="range" id="scale-slider" min="10" max="100" value="100" step="5" class="styled-range" />
              <div class="dimension-readout" id="dimension-readout">
                ${this.editor.width} &times; ${this.editor.height} px
              </div>
            </div>

            <!-- Transparency toggle -->
            <div class="control-group" id="alpha-control-group">
              <label class="checkbox-label">
                <input type="checkbox" id="alpha-checkbox" checked />
                <span>Preserve Alpha Transparency</span>
              </label>
            </div>

            <!-- Filename input -->
            <div class="control-group">
              <label class="control-label">Output Filename</label>
              <input type="text" id="filename-input" value="optimized-photo" class="styled-input" />
            </div>

            <!-- Compression summary card -->
            <div class="savings-card" id="savings-card">
              <div class="savings-header">Estimated Payload</div>
              <div class="savings-row">
                <span>Original Uncompressed:</span>
                <span id="orig-size-val">~${((this.editor.width * this.editor.height * 4) / 1024 / 1024).toFixed(1)} MB</span>
              </div>
              <div class="savings-row highlight">
                <span>Optimized Size:</span>
                <strong id="opt-size-val">Calculating...</strong>
              </div>
              <div class="savings-badge" id="savings-badge">
                Saving ~0%
              </div>
            </div>

            <!-- Modal Action Buttons -->
            <div class="modal-actions">
              <button class="btn btn-secondary" id="cancel-save-web">Cancel</button>
              <button class="btn btn-primary" id="download-save-web">
                <span>Download Optimized File</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    this.container = overlay;

    this.previewImg = overlay.querySelector('#save-web-img');
    this.sizeDisplay = overlay.querySelector('#save-web-stats');

    // Attach listeners
    overlay.querySelector('#close-save-web')?.addEventListener('click', () => this.close());
    overlay.querySelector('#cancel-save-web')?.addEventListener('click', () => this.close());

    // Format buttons
    const fmtButtons = overlay.querySelectorAll('.format-btn');
    fmtButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        fmtButtons.forEach(b => b.classList.remove('active'));
        const target = e.currentTarget as HTMLButtonElement;
        target.classList.add('active');
        this.format = target.dataset.fmt as any;

        const qualGroup = overlay.querySelector('#quality-control-group') as HTMLElement;
        const alphaGroup = overlay.querySelector('#alpha-control-group') as HTMLElement;

        if (this.format === 'png') {
          if (qualGroup) qualGroup.style.display = 'none';
        } else {
          if (qualGroup) qualGroup.style.display = 'block';
        }

        if (this.format === 'jpeg') {
          if (alphaGroup) alphaGroup.style.display = 'none';
        } else {
          if (alphaGroup) alphaGroup.style.display = 'block';
        }

        this.updatePreview();
      });
    });

    // Quality slider
    const qSlider = overlay.querySelector('#quality-slider') as HTMLInputElement;
    const qVal = overlay.querySelector('#quality-val') as HTMLElement;
    qSlider?.addEventListener('input', () => {
      this.quality = parseInt(qSlider.value);
      if (qVal) qVal.textContent = `${this.quality}%`;
      this.updatePreview();
    });

    // Scale slider
    const sSlider = overlay.querySelector('#scale-slider') as HTMLInputElement;
    const sVal = overlay.querySelector('#scale-val') as HTMLElement;
    const dimReadout = overlay.querySelector('#dimension-readout') as HTMLElement;
    sSlider?.addEventListener('input', () => {
      this.scale = parseInt(sSlider.value);
      if (sVal) sVal.textContent = `${this.scale}%`;
      const scaledW = Math.round((this.editor.width * this.scale) / 100);
      const scaledH = Math.round((this.editor.height * this.scale) / 100);
      if (dimReadout) dimReadout.innerHTML = `${scaledW} &times; ${scaledH} px`;
      this.updatePreview();
    });

    // Alpha checkbox
    const alphaBox = overlay.querySelector('#alpha-checkbox') as HTMLInputElement;
    alphaBox?.addEventListener('change', () => {
      this.preserveAlpha = alphaBox.checked;
      this.updatePreview();
    });

    // Filename
    const nameInput = overlay.querySelector('#filename-input') as HTMLInputElement;
    nameInput?.addEventListener('input', () => {
      this.filename = nameInput.value.trim() || 'image';
    });

    // Download button
    overlay.querySelector('#download-save-web')?.addEventListener('click', () => {
      if (this.currentBlob) {
        const ext = this.format === 'jpeg' ? 'jpg' : this.format;
        Exporter.downloadBlob(this.currentBlob, `${this.filename}.${ext}`);
        this.editor.showToast(`Saved optimized file: ${this.filename}.${ext}`);
        this.close();
      }
    });
  }

  private updatePreview() {
    if (!this.container) return;

    const scaledW = Math.max(10, Math.round((this.editor.width * this.scale) / 100));
    const scaledH = Math.max(10, Math.round((this.editor.height * this.scale) / 100));

    const allowAlpha = this.format !== 'jpeg' && this.preserveAlpha;
    const canvas = Exporter.getCompositeCanvas(
      this.editor,
      scaledW,
      scaledH,
      allowAlpha,
      '#ffffff'
    );

    const mime = `image/${this.format}`;
    const qual = this.format === 'png' ? 1.0 : this.quality / 100;

    canvas.toBlob((blob) => {
      if (!blob || !this.container) return;
      this.currentBlob = blob;

      if (this.previewImg) {
        this.previewImg.src = URL.createObjectURL(blob);
      }

      const sizeKb = (blob.size / 1024).toFixed(1);
      const sizeMb = (blob.size / 1024 / 1024).toFixed(2);
      const formattedSize = blob.size > 1024 * 1024 ? `${sizeMb} MB` : `${sizeKb} KB`;

      // Uncompressed raw byte estimate (width * height * 4)
      const rawBytes = this.editor.width * this.editor.height * 4;
      const savedPercent = Math.max(0, Math.round((1 - blob.size / rawBytes) * 100));

      const statsBar = this.container.querySelector('#save-web-stats');
      if (statsBar) {
        statsBar.textContent = `${this.format.toUpperCase()} · ${scaledW}×${scaledH} · ${formattedSize}`;
      }

      const optVal = this.container.querySelector('#opt-size-val');
      if (optVal) optVal.textContent = formattedSize;

      const badge = this.container.querySelector('#savings-badge');
      if (badge) {
        badge.textContent = `🚀 Saved ${savedPercent}% payload`;
      }
    }, mime, qual);
  }
}
